import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { SHARE_TOKEN } from '../utils/shareLink';
import api, { setUnauthorizedHandler } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Starts true so the app shows a loading state instead of flashing the login
  // screen before the session check comes back.
  const [loading, setLoading] = useState(!SHARE_TOKEN);
  // True only when a 401 mid-session kicked the user out (see setUnauthorizedHandler
  // below) — never on the initial not-logged-in-yet check, and never on an
  // explicit logout. Lets the Login screen say *why* it appeared instead of a
  // silent redirect that reads like the app just ate whatever the user was doing.
  const [sessionExpired, setSessionExpired] = useState(false);
  const clearSessionExpired = useCallback(() => setSessionExpired(false), []);

  // The server session is the source of truth — nothing about auth is kept in
  // localStorage, where it could be edited or drift out of sync with the cookie.
  const checkSession = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data.user);
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!SHARE_TOKEN) checkSession();   // share links never need a session
  }, [checkSession]);

  // Any 401 from a normal API call means the session went away; clear local
  // state so the app falls back to the login screen. Only fires for a session
  // that was previously live (loading is false by then) — the very first
  // /auth/me check on a fresh visit also 401s, and that is not an "expiry".
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(prevUser => {
        if (prevUser) setSessionExpired(true);
        return null;
      });
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = async (username, password, remember = false) => {
    try {
      const res = await api.post('/auth/login', { username, password, remember });
      setUser(res.data.user);
      setSessionExpired(false);
      return { success: true };
    } catch (err) {
      const status = err.response?.status;
      let error = err.response?.data?.error;
      if (!error) {
        if (!status) error = 'Cannot reach the server.';
        else if (status >= 500) error = `Server error (${status}). Check the backend logs.`;
        else error = `Sign in failed (${status}).`;
      }
      return { success: false, error };
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      // Even if the request fails, drop local state so the user is signed out.
      console.error('Logout request failed', err);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user, user, loading, login, logout,
        sessionExpired, clearSessionExpired,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
